// EXTERNAL
// LIBS
import { s3_media_upload, analytics_record } from "../../../../libs/amplify";
import { logger } from "../../../../libs/logger";
import { Google_Analytics_Event } from "../../../../libs/google-analytics";
// import { file_cleanup } from "../../../../libs/node";
// HELPERS
import create_dynamodb_creative_data from "../../../../helpers/create_dynamodb_creative_data";
import { getDomScreenshot } from "../../export-png/functions/getDomScreenshot";
import { removeCreativeTTB, saveCreativeTTB, setCreativeTTB } from "../../../../helpers/time_to_build";
import { create_new_time_isostring } from "../../../../helpers/format_time";
// TYPES
import { CreativeSave } from "../types";
// CONFIGS
import config from "../../../../configs/config";
import studio_config from "../../../../configs/studio.json";
// ASSETS
import pixelVideoPath from "../../../../assets/pixel-video.mp4";
import pixelVideoPosterPath from "../../../../assets/pixel-video-poster.jpeg";
import { ContextState } from "../../../../types/contextType";
export interface StudioConfig {
  [key: string]: any;
}
/**
 * Pixel Video asset and poster get saved to each creative onSave, this forces DV360 adserver to include rich-media reporting to creative.
 */
async function add_pixel_video(teamId: string, creativeId: string | undefined) {
  try {
    const pixelVideo = await create_file(pixelVideoPath, "pixel-video-original.mp4", "video/mp4");
    const pixelVideoPoster = await create_file(pixelVideoPosterPath, "pixel-video-poster.mp4", "image/jpeg");
    await s3_media_upload({
      fileKey: "pixel-video-original.mp4",
      mimeType: "video/mp4",
      body: pixelVideo,
      teamId,
      creativeId,
    });
    await s3_media_upload({
      fileKey: "pixel-video-poster.jpeg",
      mimeType: "image/jpeg",
      body: pixelVideoPoster,
      teamId,
      creativeId,
    });
  } catch (error) {
    console.log(error);
  }
}

const create_file = async (path: RequestInfo, name: string, mimeType: string) => {
  const response = await fetch(path);
  const data = await response.blob();
  let file = new File([data], name, {
    type: mimeType,
  });
  return file;
};

function validate_ad_creator_form(context: ContextState) {
  const format = context.metadata.format;

  try {
    // CHECK ADVERTISER
    if (context.advertiserName.length < 1) return { status: false, message: "Advertiser is not selected" };
    // CHECK ADNAME
    if (context.adName.length < 1) return { status: false, message: "Ad name is not completed" };
    // CHECK IF INVALID CHARACTERS ARE BEING USED
    if (!/^[A-Za-z0-9-_ ]*$/.test(context.adName)) return { status: false, message: "Ad name is not allowed invalid characters" };
    // CHECK BRANDNAME
    if (context.metadata.brandName.length < 1) return { status: false, message: "Brand Name is not completed" };
    // CHECK BRAND LOGO
    if (context.metadata.brandLogo.mimeType === "") return { status: false, message: "No Brand Logo uploaded" };
    // CHECK BUTTON LOGO
    if ((format === "clips" || format === "swipe") && context.metadata.button.logo.mimeType === "") return { status: false, message: `No ${studio_config.formats[format as keyof typeof studio_config.formats].inputs.buttonLogo.header} uploaded` };
    // CLICKOUT
    if (!context.metadata.button.hidden && context.metadata.button.clickout.slice(0, 4) !== "http") return { status: false, message: "Clickout link must be a valid URL" };
    // MULTIPLE UNIQUE CLICKOUTS FOR CAROUSEL
    if (format === "carousel" && context.metadata.slides.filter((slide) => !!slide.button.clickout).some((slideWithClickout) => slideWithClickout.button.clickout.slice(0, 4) !== "http")) return { status: false, message: "Slides can only have a clickout link with a valid URL" };
    // CHECK SLIDES
    // MIN
    if (context.metadata.slides.length < studio_config.formats[format as keyof typeof studio_config.formats].minSlides) return { status: false, message: "Too few frames" };
    // MAX
    if (context.metadata.slides.length > studio_config.formats[format as keyof typeof studio_config.formats].maxSlides) return { status: false, message: "Too many frames" };
    // CHECK CAROUSEL TEXT
    if (format === "carousel" && context.metadata.aspectRatio !== "mpu" && context.metadata.slides.some((slide) => !slide.content)) return { status: false, message: "Each slide must have frame text" };
    // CHECK CLIPS CREATIVE TOTAL LENGTH
    if (format === "clips" && context.metadata.slides.reduce((sum, slide) => sum + slide.media.originalVideoDuration, 0) < 6) return { status: false, message: "Clips creative needs to be at least 6 seconds long" };
    // CHECK SWIPE MATCH DESCRIPTION
    if (format === "swipe" && !context.metadata.content && context.metadata.slides.some((slide) => !slide.content)) return { status: false, message: "Match descriptions must be filled out from either the Content or Frames section" };
    return { status: true, message: "" };
  } catch (error) {
    return { status: false, message: "Something went wrong" };
  }
}

export function handleCreativeSave({ mediaLoadingDispatch, user, creative, creativeId, isNewCreative, saveExistingCreative, saveNewCreative, history, notificationDispatch }: CreativeSave): (e: React.MouseEvent<Element, MouseEvent> | undefined) => Promise<void> {
  const managedServiceTeamUser = config.managedServiceTeamIds.includes(user.teamId);
  const studioConfig: StudioConfig = studio_config;
  return async (e: React.MouseEvent<Element, MouseEvent> | undefined) => {
    (e?.currentTarget as HTMLElement).blur();
    mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Saving creative", progress: Math.floor(Math.random() * (10 - 3 + 1) + 3) } });

    Google_Analytics_Event({ name: "creative_save", category: "ad_creative", action: "save", label: creativeId });
    // Track save click
    if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
      analytics_record({
        name: "save",
        attributes: {
          eventType: "click",
          userId: `${user.id}`,
          userEmail: `${user.email}`,
          teamId: `${user.teamId}`,
          teamName: `${user.teamName}`,
          advertiserId: `${creative.advertiserId}`,
          advertiserName: `${creative.advertiserName}`,
          creativeId: `${creativeId}`,
          adName: `${creative.adName}`,
          brandName: `${creative.metadata.brandName}`,
          format: `${creative.metadata.format}`,
          assetType: `${creative.metadata.slides.find((slide: any) => slide.media.mimeType.includes("video")) ? "video" : "image"}`,
          previewURL: `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${user.teamId}/${creativeId}/index.min.html`,
        },
      });
    }
    // Track save click with emoji reactions enabled
    if (config.analytics.DISABLED === false && creative.metadata.features.reactions.visible) {
      analytics_record({
        name: `emoji-reactions-${managedServiceTeamUser ? "internal" : "external"}`,
        attributes: {
          eventType: "click",
          userId: `${user.id}`,
          userEmail: `${user.email}`,
          teamId: `${user.teamId}`,
          teamName: `${user.teamName}`,
          advertiserId: `${creative.advertiserId}`,
          advertiserName: `${creative.advertiserName}`,
          creativeId: `${creativeId}`,
          adName: `${creative.adName}`,
          brandName: `${creative.metadata.brandName}`,
          format: `${creative.metadata.format}`,
          assetType: `${creative.metadata.slides.find((slide: any) => slide.media.mimeType.includes("video")) ? "video" : "image"}`,
        },
      });
    }
    try {
      //
      // Validate user inputs
      //
      let validation = validate_ad_creator_form(creative);
      if (validation.status === false) throw new Error(validation.message);

      // KT 22.03.22: this and the object required to duplicate a creative are v similar, so use one factory function.
      const payload = create_dynamodb_creative_data(creative, user, creativeId, false);
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Saving creative", progress: Math.floor(Math.random() * (30 - 11 + 1) + 11) } });
      //
      // get dom screenshot of format
      //
      let domScreenshot: Blob = await getDomScreenshot(creative);
      await s3_media_upload({
        fileKey: "format-preview-original.png",
        mimeType: "image/png",
        body: domScreenshot,
        teamId: user.teamId,
        creativeId,
      });

      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Saving creative", progress: Math.floor(Math.random() * (60 - 31 + 1) + 31) } });
      //
      // 05/05/21 -> Added pixelVideoAsset to save video on all creatives. This forces DV360 to use Rich Media events.
      //
      await add_pixel_video(user.teamId, creativeId);

      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Saving creative", progress: Math.floor(Math.random() * (80 - 61 + 1) + 61) } });

      if (!isNewCreative) {
        // If saving an existing creative:
        await saveExistingCreative({ variables: { input: payload } });
        /**
         * HTL (09-10-23)
         * Purging any leftover Time To Build stats from LocalStorage.
         */
        removeCreativeTTB();
      }
      if (isNewCreative) {
        /**
         * HTL (09-10-23)
         * Time To Build metric.
         * 1. Save response time.
         * 2. Save creative build endTime.
         * 3. Send CreativeTTB payload to GoogleSheet.
         * 3. Purge creative from storage.
         */
        const startTime = performance.now();
        // If saving a new creative:
        await saveNewCreative({ variables: { input: payload } });
        const endTime = performance.now();
        const responseTime = endTime - startTime;
        setCreativeTTB("save", responseTime.toString());
        setCreativeTTB("endTime", create_new_time_isostring());
        saveCreativeTTB(creativeId as string, {
          id: user.id,
          teamId: user.teamId,
          teamName: user.teamName,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        });
        removeCreativeTTB();
        // Set isNewCreative to false
        history.push({
          search: "",
        });
      }
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Saving creative", progress: Math.floor(Math.random() * (99 - 98 + 1) + 98) } });
      /**
       * Creative File Cleanup.
       * crucial that this takes place after the creative has been built by the save{*}Creative functions.
       * (HTL 10-05-23) commented out "await file_cleanup()" function to counter files sometimes being deleted and not previewable by users.
       */
      // await file_cleanup({ teamId: user.teamId, creativeId });
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Success",
          message: "Creative saved successfully",
          type: "success",
          autoDismiss: true,
        },
      });
      /**
       *  (HTL - 28-04-23)
       * Increment creatives built number in user's localStorage when there's a successful save.
       * Only show in a production environment & to external teams.
       * 'creativesBuilt' matches what is used in the NPS feature.
       *
       */
      if (config.environment === "production" && user.teamId !== "pi6j") {
        let uniqueCreativeBuildCount = JSON.parse(localStorage.getItem("creativesBuilt")!);
        /**
         * Setting backup
         */
        if (uniqueCreativeBuildCount == null) uniqueCreativeBuildCount = [];
        uniqueCreativeBuildCount.push(creativeId);
        /**
         * local storage can only hold strings, hence using stringify method on this array e.g. "["1", "2", "3"]".
         */
        localStorage.setItem("creativesBuilt", JSON.stringify(uniqueCreativeBuildCount));
      }
    } catch (error) {
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      logger({ error, user, context: { origin: "HandleCreativeSave_handleCreativeSave" } });
      let errorMessage = "";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error saving creative",
          message: errorMessage,
          type: "error",
          autoDismiss: false,
        },
      });
    }
  };
}
